import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, Lock, Eye, EyeOff, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { LOGO_URL } from "../data/siteData";

export default function AdminResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";

    const [pwd, setPwd] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) {
            toast.error("Reset link is invalid or missing.");
        }
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (pwd.length < 8) {
            toast.error("Password must be at least 8 characters.");
            return;
        }
        if (pwd !== confirm) {
            toast.error("Passwords do not match.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pwd });
            setDone(true);
            setTimeout(() => navigate("/admin/login", { replace: true }), 3000);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E40AF] px-6 py-12" data-testid="admin-reset-page">
            <div className="w-full max-w-md">
                <div className="bg-white rounded-2xl shadow-2xl p-8">
                    <div className="text-center mb-8">
                        <img src={LOGO_URL} alt="Anand Tutorials" className="h-20 mx-auto mb-4" />
                        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#3B82F6]">
                            <Lock className="w-3.5 h-3.5" /> Set a New Password
                        </div>
                        <h1 className="font-heading text-2xl font-bold mt-2">Reset Your Password</h1>
                    </div>

                    {!token ? (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
                            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                            <p className="text-sm text-red-700 font-semibold">Reset link is invalid or missing.</p>
                            <Link to="/admin/forgot-password" className="text-xs text-[#1E3A8A] font-semibold hover:underline mt-3 inline-block">
                                Request a new link
                            </Link>
                        </div>
                    ) : done ? (
                        <div className="text-center space-y-3 py-4" data-testid="reset-success">
                            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
                                <CheckCircle2 className="w-7 h-7" />
                            </div>
                            <h3 className="font-heading text-lg font-bold text-[#0F172A]">Password Reset Successful</h3>
                            <p className="text-sm text-slate-600">Redirecting you to the sign-in page…</p>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <label className="block">
                                <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">New Password</span>
                                <div className="relative">
                                    <input
                                        type={showPwd ? "text" : "password"}
                                        value={pwd}
                                        onChange={(e) => setPwd(e.target.value)}
                                        required
                                        minLength={8}
                                        data-testid="reset-new-pwd"
                                        className="w-full pr-10 px-4 py-2.5 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                                    />
                                    <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700">
                                        {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                <span className="text-xs text-slate-400 mt-1 block">Minimum 8 characters. Use a mix of letters, numbers and symbols.</span>
                            </label>
                            <label className="block">
                                <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">Confirm New Password</span>
                                <input
                                    type={showPwd ? "text" : "password"}
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    required
                                    minLength={8}
                                    data-testid="reset-confirm-pwd"
                                    className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                                />
                                {confirm && pwd !== confirm && (
                                    <span className="text-xs text-red-500 mt-1 block">Passwords don't match.</span>
                                )}
                            </label>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="reset-submit"
                                className="w-full bg-[#1E3A8A] text-white font-semibold py-3 rounded-lg hover:bg-[#1E40AF] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
                            >
                                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                                {submitting ? "Resetting..." : "Set New Password"}
                            </button>
                        </form>
                    )}
                </div>
                <p className="text-center text-blue-200 text-xs mt-6">
                    © {new Date().getFullYear()} Anand Tutorials · <Link to="/admin/login" className="hover:text-amber-300">Back to Sign In</Link>
                </p>
            </div>
        </div>
    );
}
